import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

// Tailwind-aligned breakpoints: 640 sm, 768 md, 1024 lg.
const BREAKPOINTS: Array<{ min: number; cols: number }> = [
  { min: 1024, cols: 5 },
  { min: 768, cols: 4 },
  { min: 640, cols: 3 },
  { min: 0, cols: 2 },
];

function pickCols(width: number): number {
  for (const bp of BREAKPOINTS) if (width >= bp.min) return bp.cols;
  return 2;
}

function useColumnCount(): number {
  const [cols, setCols] = useState(() =>
    typeof window === "undefined" ? 5 : pickCols(window.innerWidth)
  );
  useEffect(() => {
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setCols(pickCols(window.innerWidth)));
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(raf);
    };
  }, []);
  return cols;
}

export interface StableMasonryProps<T> {
  items: T[];
  // Returns the item's aspect ratio as height/width. The component uses this
  // to predict column heights and pack items shortest-column-first. Missing
  // dimensions default to 1 (square).
  getAspectRatio: (item: T) => number;
  renderItem: (item: T, originalIndex: number) => ReactNode;
}

/**
 * Packed masonry — items go into whichever column is currently shortest.
 * This is stable under append: once an item is placed, it never moves. That
 * solves the Load More reflow bug that both CSS `columns` and
 * react-masonry-css (sequential partitioning) exhibit.
 *
 * Pinterest-style: shortest-column-first packing produces roughly balanced
 * columns without forcing any item to change columns after insertion.
 */
export function StableMasonry<T>({
  items,
  getAspectRatio,
  renderItem,
}: StableMasonryProps<T>) {
  const cols = useColumnCount();

  const columns = useMemo(() => {
    const heights = new Array<number>(cols).fill(0);
    const buckets: Array<Array<{ item: T; originalIndex: number }>> =
      Array.from({ length: cols }, () => []);

    items.forEach((item, originalIndex) => {
      const ratio = Math.max(getAspectRatio(item) || 1, 0.2); // clamp extreme ratios
      // Find shortest column (first one in case of ties — keeps stability).
      let shortest = 0;
      for (let c = 1; c < cols; c++) {
        if (heights[c] < heights[shortest]) shortest = c;
      }
      buckets[shortest].push({ item, originalIndex });
      heights[shortest] += ratio;
    });

    return buckets;
  }, [items, cols, getAspectRatio]);

  return (
    <div className="portfolio-masonry-grid">
      {columns.map((bucket, colIdx) => (
        <div key={colIdx} className="portfolio-masonry-grid-column">
          {bucket.map(({ item, originalIndex }) => (
            <div key={originalIndex}>{renderItem(item, originalIndex)}</div>
          ))}
        </div>
      ))}
    </div>
  );
}
